// ============================================
// VFXSystemRenderer - Single Particle System
// ============================================

import type { IRenderContext } from '@engine/render2d';
import { getForwardPlusOverrides } from '@engine/render2d';
import type {
  BufferHandle,
  ComputePipelineHandle,
  PipelineHandle,
  ShaderHandle,
  TextureHandle,
} from '@engine/render2d';
import { PARTICLE_STRUCT_SIZE, VFX_UNIFORMS_SIZE, VFX_VARIABLES_SIZE, VFXNotSupportedError } from './VFXTypes';
import {
  TWEAK_BUFFER_OFFSET,
  TWEAK_BUFFER_SIZE,
  tweakValueToFloats,
  type VFXTweakBinding,
} from './compiler/codegen/Tweaks';
import { generateSpawnShader, generatePrepareIndirectShader } from './compiler/WGSLEmitter';
import { VFX_BINDINGS, VFX_STORAGE_BUFFER_COUNT, VFX_COUNTER_BUFFER_SIZE, VFX_EVENT_COUNT_BUFFER_SIZE, VFX_MAX_WORKGROUPS_PER_DIMENSION } from './VFXBindings';
import { GPU_EVENT_LAYOUT, PARTICLE_LAYOUT, UNIFORMS_LAYOUT, COUNTERS_LAYOUT, VFX_RENDER_UNIFORMS_LAYOUT, type UniformsData, type CountersData } from './compiler/Layouts';
import { createStructWriter } from "@engine/render2d";
/**
 * Compiled data for a single VFX system within a graph.
 */
export interface CompiledSystemData {
  /** System ID */
  id: string;
  /** Display name */
  name: string;
  /** WGSL compute shader for particle initialization */
  initShader: string;
  /** WGSL compute shader for particle update */
  updateShader: string;
  /** WGSL/GLSL vertex shader */
  vertexShader: string;
  /** WGSL/GLSL fragment shader */
  fragmentShader: string;
  /** Maximum particles for this system */
  maxParticles: number;
  /** Spawn mode */
  spawnMode: 'direct' | 'onEvent';
  /** 
   * Spawn type for direct mode.
   * - rate: constant spawn rate
   * - variable_rate: random rate between min/max each frame
   * - single_burst: spawn once after delay
   * - periodic_burst: spawn repeatedly every period (infinite)
   * - burst: spawn count particles, repeat times with delay then interval
   */
  spawnType?: 'rate' | 'variable_rate' | 'single_burst' | 'periodic_burst' | 'burst';
  /** Event ID to trigger (if has trigger_event node) */
  outputEventId?: number;
  /** Event ID to listen to (if spawnMode === 'onEvent') */
  inputEventId?: number;
  /** Spawn count per event (for onEvent mode) */
  spawnPerEvent?: number;
  /** Spawn rate for direct mode (constant rate) */
  spawnRate?: number;
  /** Variable rate config for variable_rate spawn type */
  variableRate?: {
    rateMin: number;
    rateMax: number;
  };
  /** Burst config for burst spawn types */
  burst?: {
    count: number;
    delay: number;
    repeat: number;
    interval: number;
  };
  /** Whether fragment shader samples textures */
  usesTexture?: boolean;
  /** Texture variable slot index (0-7) if texture is used */
  textureSlot?: number;
  /** Whether this system emits light (vfx.output.light node) */
  emitsLight?: boolean;
  /** Whether the light system also renders a visible quad */
  lightRenderQuad?: boolean;
  /** Whether this system receives Forward+ lighting (vfx.output.lit_quad) */
  isLit?: boolean;
  /** Light emission configuration (when emitsLight is true) */
  lightConfig?: {
    /** Light radius in world units */
    radius: number;
    /** Light intensity multiplier */
    intensity: number;
    /** Light falloff exponent */
    falloff: number;
    /** 0 = particle color, 1 = override color */
    colorMode: number;
    /** Override color [r, g, b, a] when colorMode=1 */
    overrideColor: readonly number[];
    /** Whether particle alpha modulates intensity */
    intensityFromAlpha: boolean;
  };
  /**
   * Editor live-preview tweak slots (systems compiled with `tweakableInputs`).
   * Presence ⇒ the compute shaders read a `tweaks[]` array appended to the
   * variables buffer: the renderer sizes the buffer to TWEAK_BUFFER_SIZE,
   * seeds slots with the baked values, and `setTweak()` updates them live.
   */
  tweaks?: readonly VFXTweakBinding[];
}

/**
 * Shared resources between systems in a multi-system graph.
 * Only event-related resources are shared. Each system has its own counter buffer.
 */
export interface SharedVFXResources {
  /** Event buffer (GPU Events) - shared for inter-system communication */
  eventBuffer: BufferHandle;
  /** Event count buffer (single atomic u32) - shared for event count */
  eventCountBuffer: BufferHandle;
  /** Indirect dispatch buffer */
  indirectBuffer: BufferHandle;
  /** Max events */
  maxEvents: number;
}

/**
 * Options for VFXSystemRenderer.
 */
export interface VFXSystemRendererOptions {
  /** Workgroup size */
  workgroupSize?: number;
  /** Debug label */
  label?: string;
  /** Shared resources (for multi-system) */
  shared?: SharedVFXResources;
  /** Default texture to use when no texture is bound (e.g., from TextureManager.getBuiltin('white')) */
  defaultTexture?: TextureHandle;
}

/**
 * VFXSystemRenderer - renders a single particle system.
 * Can use shared event buffer for multi-system effects.
 */
export class VFXSystemRenderer {
  private ctx: IRenderContext;
  private label: string;
  
  // GPU Resources (own)
  private particleBuffer: BufferHandle | null = null;
  private uniformBuffer: BufferHandle | null = null;
  private variableBuffer: BufferHandle | null = null;
  /** Editor live-preview tweak slot map (null for normal literal-baked systems). */
  private _tweaks: readonly VFXTweakBinding[] | null = null;
  
  // Variable data (CPU-side, synced to GPU)
  private _variableData: Float32Array;
  
  // GPU Resources - counter buffer is always per-system
  private counterBuffer: BufferHandle | null = null;
  
  // GPU Resources - dead list buffer for particle slot recycling
  private deadListBuffer: BufferHandle | null = null;
  
  // GPU Resources (own or shared)
  private eventBuffer: BufferHandle | null = null;
  private eventCountBuffer: BufferHandle | null = null;
  private indirectBuffer: BufferHandle | null = null;
  private _ownsSharedResources: boolean = true;
  
  // Texture support
  private _textures: (TextureHandle | null)[] = [null, null, null, null, null, null, null, null];
  private _defaultTexture: TextureHandle | null = null;
  private _ownsDefaultTexture: boolean = true; // Track if we own the default texture (vs external)
  private _usesTexture: boolean = false;
  private _textureSlot: number = -1;
  
  // Pipelines
  private initPipeline: ComputePipelineHandle | null = null;
  private updatePipeline: ComputePipelineHandle | null = null;
  private spawnPipeline: ComputePipelineHandle | null = null;
  private renderPipelineHDR: PipelineHandle | null = null; // For HDR targets (rgba16float)
  private renderPipelineLDR: PipelineHandle | null = null; // For screen/LDR targets (bgra8unorm)
  private prepareIndirectPipeline: ComputePipelineHandle | null = null;
  
  // Shaders
  private initShader: ShaderHandle | null = null;
  private updateShader: ShaderHandle | null = null;
  private spawnShader: ShaderHandle | null = null;
  private renderShader: ShaderHandle | null = null;
  private prepareIndirectShader: ShaderHandle | null = null;
  
  // State
  private _systemData: CompiledSystemData;
  private _maxParticles: number;
  private _workgroupSize: number;
  private _time: number = 0;
  private _spawnOffset: number = 0;
  private _frameCount: number = 0;
  private _isDestroyed: boolean = false;
  private _emitterX: number = 0;
  private _prevEmitterX: number = 0;
  private _prevEmitterY: number = 0;
  private _emitterVelX: number = 0;
  private _emitterVelY: number = 0;
  private _hasComputedVelocity = false;
  private _emitterY: number = 0;
  private _emitterScaleX: number = 1;
  private _emitterScaleY: number = 1;
  
  // Event-driven state
  private _isEventDriven: boolean = false;
  private _inputEventId: number = 0;
  
  // Spawn rate (passed to GPU via uniforms)
  private _spawnRate: number = 0;

  // CPU-side pending spawn count. Accumulated by spawn(), consumed by _runInitPass().
  private _pendingSpawnCount: number = 0;
  
  // CPU mirror of GPU spawn accumulator for offset tracking
  private _cpuSpawnAccumulator: number = 0;
  
  constructor(
    ctx: IRenderContext,
    data: CompiledSystemData,
    options: VFXSystemRendererOptions = {}
  ) {
    this.ctx = ctx;
    this.label = options.label ?? `vfx-system-${data.id}`;
    this._systemData = data;
    
    if (!ctx.supportsCompute) {
      throw new VFXNotSupportedError('VFX System requires WebGPU compute shaders.');
    }
    
    this._maxParticles = data.maxParticles;
    this._workgroupSize = options.workgroupSize ?? 64;
    this._isEventDriven = data.spawnMode === 'onEvent';
    this._inputEventId = data.inputEventId ?? 0;
    this._spawnRate = data.spawnRate ?? 0;
    
    // Texture binding from compiled data
    this._usesTexture = data.usesTexture ?? false;
    this._textureSlot = data.textureSlot ?? -1;
    
    // Initialize variable data (384 bytes = 96 floats; +64 float4 tweak slots
    // when this system was compiled for editor live-preview) and seed tweak
    // slots with their baked values so first render matches the graph.
    this._tweaks = data.tweaks ?? null;
    this._variableData = new Float32Array((this._tweaks ? TWEAK_BUFFER_SIZE : VFX_VARIABLES_SIZE) / 4);
    if (this._tweaks) {
      for (const t of this._tweaks) {
        this._variableData.set(tweakValueToFloats(t.value, t.type), TWEAK_BUFFER_OFFSET / 4 + t.slot * 4);
      }
    }
    
    // Use shared event resources if provided
    // Counter buffer is ALWAYS per-system (for spawnOffset tracking)
    if (options.shared) {
      this.eventBuffer = options.shared.eventBuffer;
      this.eventCountBuffer = options.shared.eventCountBuffer;
      this.indirectBuffer = options.shared.indirectBuffer;
      this._ownsSharedResources = false;
    }
    
    // Use provided default texture or create one later
    if (options.defaultTexture) {
      this._defaultTexture = options.defaultTexture;
      this._ownsDefaultTexture = false;
    }
    
    this._initializeResources();
  }
  
  get systemId(): string { return this._systemData.id; }
  get maxParticles(): number { return this._maxParticles; }
  get isEventDriven(): boolean { return this._isEventDriven; }
  get inputEventId(): number { return this._inputEventId; }
  
  /** Whether this system emits light (has vfx.output.light node). */
  get emitsLight(): boolean { return this._systemData.emitsLight ?? false; }
  
  /** Whether this system receives Forward+ lighting (vfx.output.lit_quad). */
  get isLit(): boolean { return this._systemData.isLit ?? false; }
  
  /** Light emission configuration (only valid when emitsLight is true). */
  get lightConfig() { return this._systemData.lightConfig ?? null; }
  
  /** GPU particle storage buffer handle (for light injection compute pass). */
  get particleBufferHandle(): BufferHandle | null { return this.particleBuffer; }
  
  /**
   * Set emitter world position.
   */
  setPosition(x: number, y: number): void {
    // First placement is a teleport, not motion — keep prev in sync so the
    // emitter-velocity delta doesn't spike on the first frame.
    if (!this._hasComputedVelocity) {
      this._prevEmitterX = x;
      this._prevEmitterY = y;
    }
    this._emitterX = x;
    this._emitterY = y;
  }
  
  /**
   * Set emitter scale.
   */
  setScale(x: number, y: number): void {
    this._emitterScaleX = x;
    this._emitterScaleY = y;
  }
  
  /**
   * Set a texture for a variable slot.
   * @param slot - Texture slot index (0-7)
   * @param texture - Texture handle (or null to clear)
   */
  setTexture(slot: number, texture: TextureHandle | null): void {
    if (slot < 0 || slot >= 8) {
      console.warn(`[VFXSystemRenderer] Invalid texture slot ${slot}, must be 0-7`);
      return;
    }
    this._textures[slot] = texture;
  }
  
  /**
   * Set the default fallback texture used when no texture is bound to a slot.
   * By default, a 1x1 white pixel is used. Call this to use a custom texture
   * (e.g., loaded from default_texture.png).
   * @param texture - Texture handle to use as default
   */
  setDefaultTexture(texture: TextureHandle): void {
    // Destroy old default texture only if we created it (not external)
    if (this._defaultTexture && this._ownsDefaultTexture) {
      this.ctx.destroyTexture(this._defaultTexture);
    }
    this._defaultTexture = texture;
    this._ownsDefaultTexture = false; // External texture - not owned by us
  }
  
  /**
   * Get the current default fallback texture.
   */
  getDefaultTexture(): TextureHandle | null {
    return this._defaultTexture;
  }
  
  /**
   * Get current texture for a slot.
   */
  getTexture(slot: number): TextureHandle | null {
    if (slot < 0 || slot >= 8) return null;
    return this._textures[slot];
  }
  
  /**
   * Queue particles to spawn (for burst mode - bypasses GPU spawn rate).
   * For continuous spawning, use spawn rate which is handled on GPU.
   * @deprecated Use spawn rate for continuous spawning. 
   * Note: _runInitPass() handles spawning based on spawnRate automatically.
   */
  spawn(count: number): void {
    // Accumulate on CPU — _runInitPass() will write the final value to GPU once.
    // This avoids stale emitCount persisting across frames in the GPU buffer.
    const clampedCount = Math.min(count, this._maxParticles);
    if (!this._isEventDriven && clampedCount > 0) {
      this._pendingSpawnCount += clampedCount;
    }
  }
  
  // ============================================
  // VFX Variables API
  // ============================================
  
  /**
   * Set a float variable (index 0-15).
   */
  setFloat(index: number, value: number): void {
    if (index < 0 || index >= 16) return;
    this._variableData[index] = value;
    this._syncVariables();
  }
  
  /**
   * Get a float variable (index 0-15).
   */
  getFloat(index: number): number {
    if (index < 0 || index >= 16) return 0;
    return this._variableData[index];
  }
  
  /**
   * Set a vec2 variable (index 0-7).
   */
  setVec2(index: number, x: number, y: number): void {
    if (index < 0 || index >= 8) return;
    const offset = 16 + index * 2; // After 16 floats
    this._variableData[offset] = x;
    this._variableData[offset + 1] = y;
    this._syncVariables();
  }
  
  /**
   * Get a vec2 variable (index 0-7).
   */
  getVec2(index: number): { x: number; y: number } {
    if (index < 0 || index >= 8) return { x: 0, y: 0 };
    const offset = 16 + index * 2;
    return { x: this._variableData[offset], y: this._variableData[offset + 1] };
  }
  
  /**
   * Set a vec3 variable (index 0-7).
   * Stored as vec4 for GPU alignment, w component unused.
   */
  setVec3(index: number, x: number, y: number, z: number): void {
    if (index < 0 || index >= 8) return;
    const offset = 16 + 16 + index * 4; // After 16 floats + 8 vec2s (16 floats)
    this._variableData[offset] = x;
    this._variableData[offset + 1] = y;
    this._variableData[offset + 2] = z;
    this._variableData[offset + 3] = 0; // w padding
    this._syncVariables();
  }
  
  /**
   * Get a vec3 variable (index 0-7).
   */
  getVec3(index: number): { x: number; y: number; z: number } {
    if (index < 0 || index >= 8) return { x: 0, y: 0, z: 0 };
    const offset = 16 + 16 + index * 4;
    return {
      x: this._variableData[offset],
      y: this._variableData[offset + 1],
      z: this._variableData[offset + 2],
    };
  }
  
  /**
   * Set a color variable (index 0-7) as RGBA (0-1 range).
   */
  setColor(index: number, r: number, g: number, b: number, a: number = 1): void {
    if (index < 0 || index >= 8) return;
    const offset = 16 + 16 + 32 + index * 4; // After floats + vec2s + vec3s
    this._variableData[offset] = r;
    this._variableData[offset + 1] = g;
    this._variableData[offset + 2] = b;
    this._variableData[offset + 3] = a;
    this._syncVariables();
  }
  
  /**
   * Get a color variable (index 0-7).
   */
  getColor(index: number): { r: number; g: number; b: number; a: number } {
    if (index < 0 || index >= 8) return { r: 1, g: 1, b: 1, a: 1 };
    const offset = 16 + 16 + 32 + index * 4;
    return {
      r: this._variableData[offset],
      g: this._variableData[offset + 1],
      b: this._variableData[offset + 2],
      a: this._variableData[offset + 3],
    };
  }
  
  /**
   * Write an editor live-preview tweak slot (systems compiled with
   * `tweakableInputs` only). Values are pre-packed float4 contents — use
   * `tweakValueToFloats()` to convert a port value.
   */
  setTweak(slot: number, values: readonly [number, number, number, number]): void {
    if (!this._tweaks || slot < 0 || slot >= this._tweaks.length) return;
    this._variableData.set(values, TWEAK_BUFFER_OFFSET / 4 + slot * 4);
    this._syncVariables();
  }

  /** Editor live-preview tweak slot map (null for literal-baked systems). */
  get tweaks(): readonly VFXTweakBinding[] | null {
    return this._tweaks;
  }

  /**
   * Sync variable data to GPU buffer.
   */
  private _syncVariables(): void {
    if (this.variableBuffer) {
      this.ctx.updateBuffer(this.variableBuffer, this._variableData);
    }
  }

  /**
   * Update the system.
   * GPU-driven flow: Spawn → Update → Init
   * - Spawn shader calculates emitCount from spawnRate
   * - Update processes existing particles (frees dead slots)
   * - Init spawns new particles using emitCount
   */
  update(deltaTime: number): void {
    if (this._isDestroyed) return;
    
    // Clamp deltaTime to prevent particles dying instantly on first frame
    // or during long pauses (loading, tab switch, etc.)
    const dt = Math.min(deltaTime, 0.1); // Max 100ms per frame
    
    const prevTime = this._time;
    this._time += dt;
    this._updateUniforms(dt);
    
    // Reset aliveCount to 0 before update pass (update shader will increment it for alive particles)
    const aliveCountReset = new Uint32Array([0]);
    this.ctx.updateBuffer(this.counterBuffer!, aliveCountReset, COUNTERS_LAYOUT.offsets.aliveCount);
    
    // 1. Run update pass (updates particles, handles death, frees dead particle slots)
    this._runUpdatePass();
    
    // Barrier to ensure update completes
    if (this.ctx.computeBarrier) {
      this.ctx.computeBarrier();
    }
    
    // 2. Run init pass (spawns new particles)
    if (this._isEventDriven) {
      // Event-driven: prepare indirect and dispatch
      this._runPrepareIndirect();
      this._runInitPassIndirect();
      // Advance offset for event-driven systems using time-based estimation
      const spawnPerEvent = this._systemData.spawnPerEvent ?? 50;
      const estimatedEventsPerSecond = 3;
      this._cpuSpawnAccumulator += spawnPerEvent * estimatedEventsPerSecond * dt;
      const offsetIncrease = Math.floor(this._cpuSpawnAccumulator);
      if (offsetIncrease > 0) {
        this._cpuSpawnAccumulator -= offsetIncrease;
        this._spawnOffset = (this._spawnOffset + offsetIncrease) % this._maxParticles;
      }
    } else {
      // Rate-based: init reads emitCount set by spawn pass
      this._runInitPass();
      // Mirror GPU spawn accumulator logic for offset tracking
      // GPU uses fixed-point 16.16, we mirror with float
      this._cpuSpawnAccumulator += this._spawnRate * dt;
      const spawned = Math.floor(this._cpuSpawnAccumulator);
      if (spawned > 0) {
        this._cpuSpawnAccumulator -= spawned;
        this._spawnOffset = (this._spawnOffset + spawned) % this._maxParticles;
      }
    }
    
    // NOTE: Do NOT flush here - let VFXMultiSystemRenderer flush at frame end
    // Flushing here would cause race condition with event-driven systems
  }
  
  /**
   * Render particles.
   * @param viewProjection - View-projection matrix
   * @param renderCtx - Optional render context (uses stored ctx if not provided)
   * @param isHdrTarget - Whether rendering to an HDR target (rgba16float). Default false for screen.
   */
  render(viewProjection: Float32Array, renderCtx?: IRenderContext, isHdrTarget: boolean = false): void {
    // Light-only systems have no visual rendering — skip draw entirely
    // (unless renderQuad is enabled, in which case we draw AND emit light)
    if (this.emitsLight && !this._systemData.lightRenderQuad) return;
    
    const renderPipeline = isHdrTarget ? this.renderPipelineHDR : this.renderPipelineLDR;
    if (this._isDestroyed || !renderPipeline) {
      console.warn(`[VFX:${this.label}] Skip render: destroyed=${this._isDestroyed}, pipeline=${renderPipeline}`);
      return;
    }
    
    // Use provided ctx or fall back to stored ctx
    // Note: Works with shared contexts since they share resource maps
    const ctx = renderCtx ?? this.ctx;
    
    this._frameCount++;
    
    // NOTE: Render target should already be set by the caller.
    // Do NOT call setRenderTarget here - it would reset to backbuffer
    // when we may want to render to an offscreen target for post-processing.
    
    // Push debug group
    ctx.pushDebugGroup?.(`VFX:Render:${this.label}`);
    
    ctx.setPipeline(renderPipeline);
    ctx.setUniform('viewProjection', viewProjection);
    ctx.setUniform('time', this._time);
    ctx.setUniform('emitterX', this._emitterX);
    ctx.setUniform('emitterY', this._emitterY);
    ctx.setUniform('emitterScaleX', this._emitterScaleX);
    ctx.setUniform('emitterScaleY', this._emitterScaleY);
    
    // Bind texture if this system uses textures
    if (this._usesTexture) {
      // Use user-bound texture or default texture (if set)
      const texture = (this._textureSlot >= 0 ? this._textures[this._textureSlot] : null) ?? this._defaultTexture;
      if (texture) {
        ctx.bindTexture(0, texture);
      }
    }
    // Note: If _usesTexture is false, pipeline doesn't have texture bindings - don't bind anything
    
    if (ctx.bindUniforms) {
      ctx.bindUniforms();
    }
    
    ctx.bindVertexBuffer(this.particleBuffer!);
    
    if (ctx.drawInstanced) {
      ctx.drawInstanced(6, this._maxParticles, 0, 0);
    } else {
      console.warn(`[VFX:${this.label}] drawInstanced not available!`);
    }
    
    ctx.popDebugGroup?.();
  }
  
  /**
   * Reset all particles.
   */
  reset(): void {
    if (this._isDestroyed) return;
    
    this._time = 0;
    this._spawnOffset = 0;
    this._pendingSpawnCount = 0;
    
    // Reset particle buffer
    const zeros = new Float32Array(this._maxParticles * (PARTICLE_STRUCT_SIZE / 4));
    this.ctx.updateBuffer(this.particleBuffer!, zeros);
    
    // Reset counter buffer with deadCount = maxParticles (all slots free)
    if (this.counterBuffer) {
      this._counterWriter
        .clear()
        .set('aliveCount', 0)
        .set('deadCount', this._maxParticles)
        .set('emitCount', 0)
        .set('_unused', 0)
        .set('spawnAccumulator', 0)
        .set('spawnOffset', 0);
      this.ctx.updateBuffer(this.counterBuffer, this._counterWriter.uint32);
    }
    
    // Reset dead list with all indices
    if (this.deadListBuffer) {
      const deadListReset = new Uint32Array(this._maxParticles);
      for (let i = 0; i < this._maxParticles; i++) {
        deadListReset[i] = i;
      }
      this.ctx.updateBuffer(this.deadListBuffer, deadListReset);
    }
  }
  
  /**
   * Destroy resources.
   */
  destroy(): void {
    if (this._isDestroyed) return;
    this._isDestroyed = true;
    
    // Destroy own buffers
    if (this.particleBuffer) this.ctx.destroyBuffer(this.particleBuffer);
    if (this.uniformBuffer) this.ctx.destroyBuffer(this.uniformBuffer);
    if (this.variableBuffer) this.ctx.destroyBuffer(this.variableBuffer);
    
    // Counter buffer is always per-system
    if (this.counterBuffer) this.ctx.destroyBuffer(this.counterBuffer);
    
    // Dead list buffer is always per-system
    if (this.deadListBuffer) this.ctx.destroyBuffer(this.deadListBuffer);
    
    // Destroy default texture only if we own it (internally created)
    if (this._defaultTexture && this._ownsDefaultTexture) {
      this.ctx.destroyTexture(this._defaultTexture);
    }
    
    // Only destroy shared resources if we own them
    if (this._ownsSharedResources) {
      if (this.eventCountBuffer) this.ctx.destroyBuffer(this.eventCountBuffer);
      if (this.eventBuffer) this.ctx.destroyBuffer(this.eventBuffer);
      if (this.indirectBuffer) this.ctx.destroyBuffer(this.indirectBuffer);
    }
    
    // Destroy pipelines
    if (this.initPipeline && this.ctx.destroyComputePipeline) {
      this.ctx.destroyComputePipeline(this.initPipeline);
    }
    if (this.updatePipeline && this.ctx.destroyComputePipeline) {
      this.ctx.destroyComputePipeline(this.updatePipeline);
    }
    if (this.spawnPipeline && this.ctx.destroyComputePipeline) {
      this.ctx.destroyComputePipeline(this.spawnPipeline);
    }
    if (this.prepareIndirectPipeline && this.ctx.destroyComputePipeline) {
      this.ctx.destroyComputePipeline(this.prepareIndirectPipeline);
    }
    if (this.renderPipelineHDR) {
      this.ctx.destroyPipeline(this.renderPipelineHDR);
    }
    if (this.renderPipelineLDR) {
      this.ctx.destroyPipeline(this.renderPipelineLDR);
    }
    
    // Destroy shaders
    if (this.initShader) this.ctx.destroyShader(this.initShader);
    if (this.updateShader) this.ctx.destroyShader(this.updateShader);
    if (this.spawnShader) this.ctx.destroyShader(this.spawnShader);
    if (this.prepareIndirectShader) this.ctx.destroyShader(this.prepareIndirectShader);
    if (this.renderShader) this.ctx.destroyShader(this.renderShader);
  }
  
  // ============================================
  // Private
  // ============================================
  
  private _initializeResources(): void {
    // Particle buffer
    this.particleBuffer = this.ctx.createBuffer({
      size: this._maxParticles * PARTICLE_STRUCT_SIZE,
      usage: ['storage', 'vertex', 'copy-dst'],
      label: `${this.label}-particles`,
    });
    
    const zeros = new Float32Array(this._maxParticles * (PARTICLE_STRUCT_SIZE / 4));
    this.ctx.updateBuffer(this.particleBuffer, zeros);
    
    // Uniform buffer
    this.uniformBuffer = this.ctx.createBuffer({
      size: VFX_UNIFORMS_SIZE,
      usage: ['uniform', 'copy-dst'],
      label: `${this.label}-uniforms`,
    });
    
    // Variable buffer (for exposed parameters; larger when the system carries
    // editor live-preview tweak slots — the shader-side struct grows to match)
    this.variableBuffer = this.ctx.createBuffer({
      size: this._variableData.byteLength,
      usage: ['uniform', 'copy-dst'],
      label: `${this.label}-variables`,
    });
    this.ctx.updateBuffer(this.variableBuffer, this._variableData);
    
    // Counter buffer is ALWAYS per-system (6 u32: aliveCount, deadCount, emitCount, _unused, spawnAccumulator, spawnOffset)
    // Note: index 3 was eventCount, now unused in per-system buffer (eventCount is in shared eventCountBuffer)
    this.counterBuffer = this.ctx.createBuffer({
      size: VFX_COUNTER_BUFFER_SIZE,
      usage: ['storage', 'copy-dst', 'copy-src'],
      label: `${this.label}-counter`,
    });
    
    // Initialize counter buffer with deadCount = maxParticles (all slots are free)
    this._counterWriter
      .clear()
      .set('aliveCount', 0)
      .set('deadCount', this._maxParticles)
      .set('emitCount', 0)
      .set('_unused', 0)
      .set('spawnAccumulator', 0)
      .set('spawnOffset', 0);
    this.ctx.updateBuffer(this.counterBuffer, this._counterWriter.uint32);
    
    // Dead list buffer - array of free slot indices for particle recycling
    // Initialize with all indices: [0, 1, 2, ..., maxParticles-1]
    this.deadListBuffer = this.ctx.createBuffer({
      size: this._maxParticles * 4, // array of u32 indices
      usage: ['storage', 'copy-dst'],
      label: `${this.label}-deadlist`,
    });
    
    // Fill dead list with all indices (all slots are free initially)
    const deadListInit = new Uint32Array(this._maxParticles);
    for (let i = 0; i < this._maxParticles; i++) {
      deadListInit[i] = i;
    }
    this.ctx.updateBuffer(this.deadListBuffer, deadListInit);
    
    // Create own event resources if not provided (single-system mode)
    if (this._ownsSharedResources) {
      // Event count buffer (single atomic u32) - shared between systems in multi-system
      this.eventCountBuffer = this.ctx.createBuffer({
        size: VFX_EVENT_COUNT_BUFFER_SIZE,
        usage: ['storage', 'copy-dst', 'copy-src'],
        label: `${this.label}-eventcount`,
      });
      // Initialize to 0
      this.ctx.updateBuffer(this.eventCountBuffer, new Uint32Array([0]));
      
      // Event buffer
      const MAX_EVENTS = 1024;
      this.eventBuffer = this.ctx.createBuffer({
        size: MAX_EVENTS * GPU_EVENT_LAYOUT.size,
        usage: ['storage', 'copy-dst'],
        label: `${this.label}-events`,
      });
      // Zero-initialize event buffer to prevent garbage reads
      this.ctx.updateBuffer(this.eventBuffer, new Float32Array(MAX_EVENTS * GPU_EVENT_LAYOUT.size / 4));
      
      // Indirect buffer (3 u32: x, y, z workgroup counts)
      this.indirectBuffer = this.ctx.createBuffer({
        size: 12,
        usage: ['storage', 'indirect', 'copy-dst'],
        label: `${this.label}-indirect`,
      });
    }
    
    this._createPipelines();
  }
  
  private _createPipelines(): void {
    if (!this.ctx.createComputeShader || !this.ctx.createComputePipeline) return;
    
    // Spawn pipeline (GPU-driven spawn control)
    // Note: spawn shader only uses binding 0,2 but we include all 4 storage buffers
    // for layout compatibility
    const spawnShaderSource = generateSpawnShader();
    this.spawnShader = this.ctx.createComputeShader(spawnShaderSource);
    this.spawnPipeline = this.ctx.createComputePipeline({
      shader: this.spawnShader,
      entryPoint: 'main',
      workgroupSize: [1, 1, 1],
      storageBufferCount: VFX_STORAGE_BUFFER_COUNT,
      label: `${this.label}-spawn`,
    });
    
    // Init pipeline (includes texture bindings for point maps)
    this.initShader = this.ctx.createComputeShader(this._systemData.initShader);
    this.initPipeline = this.ctx.createComputePipeline({
      shader: this.initShader,
      entryPoint: 'main',
      workgroupSize: [this._workgroupSize, 1, 1],
      storageBufferCount: VFX_STORAGE_BUFFER_COUNT,
      textureCount: 8, // Support up to 8 texture variables
      label: `${this.label}-init`,
    });
    
    // Update pipeline (includes texture bindings for point maps)
    this.updateShader = this.ctx.createComputeShader(this._systemData.updateShader);
    this.updatePipeline = this.ctx.createComputePipeline({
      shader: this.updateShader,
      entryPoint: 'main',
      workgroupSize: [this._workgroupSize, 1, 1],
      storageBufferCount: VFX_STORAGE_BUFFER_COUNT,
      textureCount: 8, // Support up to 8 texture variables
      label: `${this.label}-update`,
    });
    
    // Prepare indirect pipeline (for event-driven systems)
    if (this._isEventDriven) {
      const prepareShader = this._getPrepareIndirectShader();
      this.prepareIndirectShader = this.ctx.createComputeShader(prepareShader);
      this.prepareIndirectPipeline = this.ctx.createComputePipeline({
        shader: this.prepareIndirectShader,
        entryPoint: 'main',
        workgroupSize: [1, 1, 1],
        storageBufferCount: VFX_STORAGE_BUFFER_COUNT,
        label: `${this.label}-prepare-indirect`,
      });
    }
    
    // Render pipeline - create both HDR (rgba16float) and LDR (swap chain) versions
    this.renderShader = this.ctx.createShader(
      this._systemData.vertexShader,
      this._systemData.fragmentShader
    );
    
    const vertexBufferDesc = {
      stride: PARTICLE_STRUCT_SIZE,
      stepMode: 'instance' as const,
      attributes: [
        { name: 'position', format: 'float32x3' as const, offset: PARTICLE_LAYOUT.offsets.position, location: 0 },
        { name: 'velocity', format: 'float32x3' as const, offset: PARTICLE_LAYOUT.offsets.velocity, location: 1 },
        { name: 'color', format: 'float32x4' as const, offset: PARTICLE_LAYOUT.offsets.color, location: 2 },
        { name: 'size', format: 'float32' as const, offset: PARTICLE_LAYOUT.offsets.size, location: 3 },
        { name: 'scale', format: 'float32x2' as const, offset: PARTICLE_LAYOUT.offsets.scale, location: 4 },
        { name: 'rotation', format: 'float32' as const, offset: PARTICLE_LAYOUT.offsets.rotation, location: 5 },
        { name: 'lifetime', format: 'float32' as const, offset: PARTICLE_LAYOUT.offsets.lifetime, location: 6 },
        { name: 'maxLifetime', format: 'float32' as const, offset: PARTICLE_LAYOUT.offsets.maxLifetime, location: 7 },
        { name: 'alive', format: 'uint32' as const, offset: PARTICLE_LAYOUT.offsets.alive, location: 8 },
      ],
    };
    
    const blendState = this.isLit
      ? {
          // Lit particles: standard alpha blending (lighting already provides brightness)
          color: { srcFactor: 'src-alpha' as const, dstFactor: 'one-minus-src-alpha' as const, operation: 'add' as const },
          alpha: { srcFactor: 'one' as const, dstFactor: 'one-minus-src-alpha' as const, operation: 'add' as const },
        }
      : {
          // Unlit particles: additive blending (glow/fire/sparks)
          color: { srcFactor: 'src-alpha' as const, dstFactor: 'one' as const, operation: 'add' as const },
          alpha: { srcFactor: 'one' as const, dstFactor: 'one' as const, operation: 'add' as const },
        };
    
    const primitiveState = {
      topology: 'triangle-list' as const,
      cullMode: 'none' as const,
      usesTextures: this._usesTexture,
    };
    
    // Only pass override constants when the shader actually has the override declarations
    // (lit + textured path generates them; lit-only without texture does not)
    const litOverrides = (this.isLit && this._usesTexture) ? getForwardPlusOverrides() : undefined;

    // HDR pipeline (rgba16float) - for rendering to HDR offscreen targets
    const hdrFormat = this.ctx.backend === 'webgpu' ? 'rgba16float' : this.ctx.getSwapChainFormat();
    this.renderPipelineHDR = this.ctx.createPipeline({
      shader: this.renderShader,
      vertexBuffers: [vertexBufferDesc],
      colorTargets: [{ format: hdrFormat, blend: blendState }],
      primitive: primitiveState,
      uniformDef: VFX_RENDER_UNIFORMS_LAYOUT,
      hasForwardPlus: this.isLit,
      constants: litOverrides,
      label: `${this.label}-render-hdr`,
    });
    
    // LDR pipeline (swap chain format) - for direct screen rendering
    this.renderPipelineLDR = this.ctx.createPipeline({
      shader: this.renderShader,
      vertexBuffers: [vertexBufferDesc],
      colorTargets: [{ format: this.ctx.getSwapChainFormat(), blend: blendState }],
      primitive: primitiveState,
      uniformDef: VFX_RENDER_UNIFORMS_LAYOUT,
      hasForwardPlus: this.isLit,
      constants: litOverrides,
      label: `${this.label}-render-ldr`,
    });
    
    // Create default white texture for fallback only if:
    // 1. System uses textures
    // 2. No texture was provided via options
    if (this._usesTexture && !this._defaultTexture) {
      this._defaultTexture = this.ctx.createTexture({
        width: 1,
        height: 1,
        format: 'rgba8unorm',
        usage: ['sampled', 'copy-dst'],
        label: `${this.label}-default-tex`,
      });
      this.ctx.updateTexture(this._defaultTexture, new Uint8Array([255, 255, 255, 255]));
      this._ownsDefaultTexture = true;
    }
  }
  
  private _getPrepareIndirectShader(): string {
    const spawnPerEvent = this._systemData.spawnPerEvent ?? 10;
    return generatePrepareIndirectShader(spawnPerEvent);
  }
  
  // Reusable writers (avoid allocation per frame)
  private _uniformWriter = createStructWriter<UniformsData>(UNIFORMS_LAYOUT);
  private _counterWriter = createStructWriter<CountersData>(COUNTERS_LAYOUT);
  
  private _updateUniforms(deltaTime: number): void {
    // Identity matrices for view/projection (actual matrices set during render)
    const identityMatrix = new Float32Array([
      1, 0, 0, 0,
      0, 1, 0, 0,
      0, 0, 1, 0,
      0, 0, 0, 1,
    ]);

    // Emitter velocity from per-frame position deltas (world units/sec) —
    // feeds the `emitterVelocity` env builtin (Inherit Velocity node).
    if (deltaTime > 0) {
      this._emitterVelX = (this._emitterX - this._prevEmitterX) / deltaTime;
      this._emitterVelY = (this._emitterY - this._prevEmitterY) / deltaTime;
      this._hasComputedVelocity = true;
    }
    this._prevEmitterX = this._emitterX;
    this._prevEmitterY = this._emitterY;

    this._uniformWriter
      .set('time', this._time)
      .set('deltaTime', deltaTime)
      .set('particleCount', 0) // reserved (now in counters.emitCount)
      .set('maxParticles', this._maxParticles)
      .set('spawnOffset', this._spawnOffset)
      .set('emitterX', this._emitterX)
      .set('emitterY', this._emitterY)
      .set('spawnRate', this._spawnRate)
      .set('emitterScaleX', this._emitterScaleX)
      .set('emitterScaleY', this._emitterScaleY)
      .set('viewMatrix', identityMatrix)
      .set('projMatrix', identityMatrix)
      .set('emitterVelX', this._emitterVelX)
      .set('emitterVelY', this._emitterVelY);

    this.ctx.updateBuffer(this.uniformBuffer!, this._uniformWriter.float32);
  }
  
  private _runSpawnPass(): void {
    if (!this.spawnPipeline || !this.ctx.setComputePipeline || !this.ctx.bindComputeResources || !this.ctx.dispatchCompute) {
      console.warn(`[VFXSystem:${this.label}] Spawn pass skipped - missing pipeline or context methods`);
      return;
    }
    
    this.ctx.setComputePipeline(this.spawnPipeline);
    // Spawn shader uses binding 0 (uniforms) and binding 2 (counters)
    // storageBuffers[1] maps to binding 2
    this.ctx.bindComputeResources({
      storageBuffers: [
        { buffer: this.particleBuffer!, offset: 0 }, // binding 1 (unused but required)
        { buffer: this.counterBuffer!, offset: 0 },  // binding 2 (counters)
        { buffer: this.eventBuffer!, offset: 0 },    // binding 3 (unused but required)
        { buffer: this.eventCountBuffer!, offset: 0 }, // binding 5 (unused but required)
        { buffer: this.deadListBuffer!, offset: 0 }, // binding 6 (unused but required)
      ],
      uniforms: [this.uniformBuffer!, this.variableBuffer!],
    });
    
    this.ctx.dispatchCompute({ x: 1, y: 1, z: 1 });
    
    if (this.ctx.computeBarrier) {
      this.ctx.computeBarrier();
    }
  }
  
  private _runInitPass(): void {
    if (!this.ctx.setComputePipeline || !this.ctx.bindComputeResources || !this.ctx.dispatchCompute || !this.initPipeline) {
      console.warn(`[VFX:${this.label}] _runInitPass: missing required methods/pipeline`);
      return;
    }
    
    // Write emitCount from CPU-tracked pending count, then clear it.
    // This ensures emitCount is always fresh per frame — no stale values
    // from previous frames persisting in the GPU buffer.
    const emitCount = Math.min(this._pendingSpawnCount, this._maxParticles);
    this._pendingSpawnCount = 0;
    
    const emitCountData = new Uint32Array([emitCount]);
    this.ctx.updateBuffer(this.counterBuffer!, emitCountData, COUNTERS_LAYOUT.offsets.emitCount);
    
    // Skip dispatch entirely if nothing to spawn (save GPU work)
    if (emitCount === 0) {
      return;
    }
    
    // Dispatch enough workgroups to cover the spawn count
    const numWorkgroups = Math.min(
      Math.ceil(emitCount / this._workgroupSize),
      VFX_MAX_WORKGROUPS_PER_DIMENSION
    );
    
    this.ctx.setComputePipeline(this.initPipeline);
    this.ctx.bindComputeResources({
      storageBuffers: [
        { buffer: this.particleBuffer!, offset: 0 },
        { buffer: this.counterBuffer!, offset: 0 },
        { buffer: this.eventBuffer!, offset: 0 },
        { buffer: this.eventCountBuffer!, offset: 0 },
        { buffer: this.deadListBuffer!, offset: 0 },
      ],
      uniforms: [this.uniformBuffer!, this.variableBuffer!],
      // Bind texture variables for point maps / texture sampling in init shader
      textures: this._textures.map(t => t ?? this._defaultTexture!) as TextureHandle[],
    });
    
    this.ctx.dispatchCompute({ x: numWorkgroups, y: 1, z: 1 });
    
    if (this.ctx.computeBarrier) {
      this.ctx.computeBarrier();
    }
  }
  
  private _spawnAccumulator: number = 0;
  
  private _runPrepareIndirect(): void {
    if (!this.prepareIndirectPipeline || !this.ctx.setComputePipeline) return;
    
    // Note: No debug groups in compute passes - computeBarrier ends the pass which destroys groups
    
    this.ctx.setComputePipeline(this.prepareIndirectPipeline);
    this.ctx.bindComputeResources!({
      storageBuffers: [
        { buffer: this.particleBuffer!, offset: 0 },
        { buffer: this.counterBuffer!, offset: 0 },
        { buffer: this.eventBuffer!, offset: 0 },
        { buffer: this.eventCountBuffer!, offset: 0 },
        { buffer: this.deadListBuffer!, offset: 0 },
      ],
      uniforms: [this.uniformBuffer!, this.variableBuffer!],
    });
    
    this.ctx.dispatchCompute!({ x: 1, y: 1, z: 1 });
    
    if (this.ctx.computeBarrier) {
      this.ctx.computeBarrier();
    }
  }
  
  private _runInitPassIndirect(): void {
    if (!this.initPipeline) return;
    
    // Note: No debug groups in compute passes - computeBarrier ends the pass which destroys groups
    
    // For event-driven systems, dispatch enough workgroups to handle maxParticles
    // The init shader will check emitCount to limit actual spawns
    const numWorkgroups = Math.ceil(this._maxParticles / this._workgroupSize);
    
    this.ctx.setComputePipeline!(this.initPipeline!);
    this.ctx.bindComputeResources!({
      storageBuffers: [
        { buffer: this.particleBuffer!, offset: 0 },
        { buffer: this.counterBuffer!, offset: 0 },
        { buffer: this.eventBuffer!, offset: 0 },
        { buffer: this.eventCountBuffer!, offset: 0 },
        { buffer: this.deadListBuffer!, offset: 0 },
      ],
      uniforms: [this.uniformBuffer!, this.variableBuffer!],
      // Bind texture variables for point maps / texture sampling in init shader
      textures: this._textures.map(t => t ?? this._defaultTexture!) as TextureHandle[],
    });
    
    this.ctx.dispatchCompute!({ x: numWorkgroups, y: 1, z: 1 });
    
    if (this.ctx.computeBarrier) {
      this.ctx.computeBarrier();
    }
  }
  
  private _runUpdatePass(): void {
    if (!this.ctx.setComputePipeline || !this.ctx.bindComputeResources || !this.ctx.dispatchCompute) return;
    
    // Note: No debug groups in compute passes - computeBarrier ends the pass which destroys groups
    
    this.ctx.setComputePipeline(this.updatePipeline!);
    this.ctx.bindComputeResources({
      storageBuffers: [
        { buffer: this.particleBuffer!, offset: 0 },
        { buffer: this.counterBuffer!, offset: 0 },
        { buffer: this.eventBuffer!, offset: 0 },
        { buffer: this.eventCountBuffer!, offset: 0 },
        { buffer: this.deadListBuffer!, offset: 0 },
      ],
      uniforms: [this.uniformBuffer!, this.variableBuffer!],
      // Bind texture variables for point maps / texture sampling in update shader
      textures: this._textures.map(t => t ?? this._defaultTexture!) as TextureHandle[],
    });
    
    const numWorkgroups = Math.ceil(this._maxParticles / this._workgroupSize);
    this.ctx.dispatchCompute({ x: numWorkgroups, y: 1, z: 1 });
    
    if (this.ctx.computeBarrier) {
      this.ctx.computeBarrier();
    }
  }
}
